import type { ListPlanPricesResponse } from "@wallyo/contracts/console/billing";
import { t } from "../../i18n";
import { formatCountry, formatDateTime, formatMoney } from "../../i18n/format";
import {
  groupPlanPrices,
  planLabel,
  unpricedPlans,
  PRICE_STATE_TONE,
  type BillingListState,
  type CountryPriceGroup,
  type PlanPriceGroup,
  type PriceRow,
} from "./billing-model";
import { FailedCard, LoadingCard, RefreshBanner } from "./BillingNotices";

export type PricesViewProps = {
  state: BillingListState<ListPlanPricesResponse>;
  canSetPrice: boolean;
  now: Date;
  onSetPrice: () => void;
  onRetry: () => void;
};

/**
 * Prix: every country's plan prices, newest version first, the one in force
 * on top. A version set for later reads "programmé"; the ones it replaced
 * stay, muted, so any renewal can still be explained (ADR-0018 §4).
 */
export function PricesView({ state, canSetPrice, now, onSetPrice, onRetry }: PricesViewProps) {
  if (!state.data && state.status === "failed") {
    return <FailedCard text={t("billing.prices.loadFailed")} error={state.error} onRetry={onRetry} />;
  }
  if (!state.data) return <LoadingCard text={t("billing.prices.loading")} />;

  const countries = groupPlanPrices(state.data, now);
  const unpriced = unpricedPlans(state.data);
  return (
    <div className="card">
      <RefreshBanner show={state.status === "failed"} onRetry={onRetry} />
      <p className="foot">{t("billing.prices.intro")}</p>
      <SetPriceButton show={canSetPrice} onSetPrice={onSetPrice} />
      <Unpriced plans={unpriced} />
      <NoPrices show={countries.length === 0} />
      {countries.map((country) => (
        <CountryTable key={country.country} group={country} />
      ))}
    </div>
  );
}

function SetPriceButton({ show, onSetPrice }: { show: boolean; onSetPrice: () => void }) {
  if (!show) return null;
  return (
    <div className="btns">
      <button className="btn primary" type="button" data-action="setPrice" onClick={onSetPrice}>
        {t("billing.action.setPrice")}
      </button>
    </div>
  );
}

function NoPrices({ show }: { show: boolean }) {
  if (!show) return null;
  return (
    <p className="foot" role="status" data-empty="prices">
      {t("billing.prices.empty")}
    </p>
  );
}

function Unpriced({ plans }: { plans: readonly { feeTierId: string; name: string }[] }) {
  if (plans.length === 0) return null;
  return (
    <p className="foot" data-unpriced="">
      {t("billing.prices.unpriced", { plans: plans.map((plan) => plan.name).join(", ") })}
    </p>
  );
}

function CountryTable({ group }: { group: CountryPriceGroup }) {
  return (
    <div className="table-wrap" data-country={group.country}>
      <h2 className="verif-h">{formatCountry(group.country)}</h2>
      <table className="customer-settings">
        <thead>
          <tr>
            <th>{t("billing.col.validFrom")}</th>
            <th className="num">{t("billing.col.amount")}</th>
            <th>{t("billing.col.currency")}</th>
            <th>{t("billing.col.state")}</th>
            <th>{t("billing.col.setBy")}</th>
            <th>{t("billing.col.reason")}</th>
          </tr>
        </thead>
        <tbody>
          {group.plans.map((plan) => (
            <PlanRows key={plan.feeTierId} plan={plan} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PlanRows({ plan }: { plan: PlanPriceGroup }) {
  return (
    <>
      <tr className="cat">
        <td colSpan={6}>{planLabel(plan.plan, plan.feeTierId)}</td>
      </tr>
      {plan.rows.map((row) => (
        <PriceLine key={row.price.planPriceId} row={row} />
      ))}
    </>
  );
}

function PriceLine({ row }: { row: PriceRow }) {
  const { price, state } = row;
  let cell = "";
  if (state === "superseded") cell = "muted-cell";
  return (
    <tr data-price={price.planPriceId} data-state={state}>
      <td className={cell}>{formatDateTime(price.validFrom)}</td>
      <td className={`num ${cell}`}>{formatMoney(price.amount, price.currency)}</td>
      <td className={`mono ${cell}`}>{price.currency}</td>
      <td>
        <span className={`pill ${PRICE_STATE_TONE[state]}`}>{t(`billing.priceState.${state}` as const)}</span>
      </td>
      <td className={cell}>
        <SetBy name={price.setBy} />
      </td>
      <td className={cell}>
        <Reason reason={price.reason} />
      </td>
    </tr>
  );
}

function SetBy({ name }: { name: string | null }) {
  if (!name) return <span className="meta">{t("billing.prices.imported")}</span>;
  return <bdi>{name}</bdi>;
}

function Reason({ reason }: { reason: string | null }) {
  if (!reason) return <span className="meta">—</span>;
  return <bdi>{reason}</bdi>;
}
